import { Link, Navigate, useNavigate } from 'react-router-dom'
import { format, parseISO } from 'date-fns'

import { requestStateToColorClass } from '~shared/constants'
import { DashboardRequestDetails } from '~shared/types'
import { camelToWords } from '~shared/utils'

import { routes } from '~constants/routes'
import { classNames } from '~utils/index'

import { RequestSkeleton } from '~features/request/components/RequestSkeleton'
import { useGetAllRequestsQuery } from '~features/request/queries'

const tableHeaders = [
  'Created',
  'Requestor',
  'Type',
  'Goods and Services',
  'Value',
  'Status',
  'Stage',
]

const renderTableHeaders = () => {
  return (
    <thead>
      <tr>
        {tableHeaders.map((header) => (
          <th
            key={header}
            scope="col"
            className="py-3 pr-3 text-left text-xs font-medium uppercase tracking-wide text-gray-500"
          >
            {header}
          </th>
        ))}
      </tr>
    </thead>
  )
}

const renderEmptyState = () => {
  return (
    <div className="flex justify-center items-center my-auto">
      <div className="text-center">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            vectorEffect="non-scaling-stroke"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
          />
        </svg>
        <h3 className="mt-2 text-sm font-semibold text-gray-900">
          No requests
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Get started by creating a new request.
        </p>
        <div className="mt-6">
          <button
            type="button"
            className="inline-flex items-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            <svg
              className="-ml-0.5 mr-1.5 h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
            </svg>
            <Link to="/requests/create">Create new request</Link>
          </button>
        </div>
      </div>
    </div>
  )
}

const Table = ({ data }: { data: DashboardRequestDetails[] }) => {
  const navigate = useNavigate()
  return (
    <div className="mt-8 flow-root ml-4 sm:ml-0">
      <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
          <table className="min-w-full divide-y divide-gray-300">
            {renderTableHeaders()}
            <tbody className="divide-y divide-gray-200 bg-white">
              {data?.map((request: DashboardRequestDetails) => (
                <tr
                  key={request.id}
                  className="hover:bg-slate-100 hover:cursor-pointer"
                  onClick={() => navigate(`/requests/${request.id}`)}
                >
                  <td
                    key={request.createdAt.toString()}
                    className="whitespace-nowrap py-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0 max-w-sm"
                  >
                    {format(
                      parseISO(request.createdAt.toString()),
                      'dd-MMM-yyyy',
                    )}
                  </td>
                  <td
                    key={request.type}
                    className="whitespace-nowrap py-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0 max-w-sm"
                  >
                    {request.requestor}
                  </td>
                  <td
                    key={request.type}
                    className="whitespace-nowrap py-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0 max-w-sm"
                  >
                    {request.type}
                  </td>
                  <td
                    key={request.goodAndService}
                    className="whitespace-nowrap py-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0 max-w-sm truncate"
                  >
                    {request.goodAndService}
                  </td>
                  <td
                    key={request.value}
                    className="whitespace-nowrap py-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0 max-w-sm"
                  >
                    {Number(request.value).toFixed(2)}
                  </td>
                  <td
                    key={request.state}
                    className="whitespace-nowrap py-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0 max-w-sm"
                  >
                    <span
                      className={classNames(
                        'inline-flex items-center rounded-md px-2 py-1 text-xs font-medium',
                        requestStateToColorClass(request.state),
                      )}
                    >
                      {camelToWords(request.state)}
                    </span>
                  </td>
                  <td
                    key={request.stageName}
                    className="whitespace-nowrap py-4 pr-3 text-sm font-medium text-gray-900 sm:pl-0 max-w-sm"
                  >
                    {request.stageName}
                  </td>

                  <td className="whitespace-nowrap py-4 pr-3 text-sm font-medium text-primary sm:pl-0 max-w-sm">
                    <Link to={`/requests/${request.id}`}>View</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

export const RequestsTable = () => {
  const { isLoading, data, error } = useGetAllRequestsQuery()

  if (isLoading) {
    return <RequestSkeleton />
  }

  if (error) {
    const typedError = error as { message: string; status: number }
    return (
      <Navigate
        to={routes.error}
        replace
        state={{
          message: typedError?.message,
          status: typedError?.status,
        }}
      />
    )
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {!data || data.length === 0 ? renderEmptyState() : null}
      {data && data.length > 0 && (
        <>
          <div className="sm:flex sm:items-center">
            <div className="sm:flex-auto">
              <h1 className="text-4xl font-light leading-6 text-black">
                Requests
              </h1>
            </div>
            <div className="mt-12 sm:ml-16 sm:mt-0 sm:flex-none">
              <button
                type="button"
                className="block w-52 h-12 rounded-md bg-primary px-4 py-2 text-center text-lg font-semibold text-white shadow-sm hover:bg-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
              >
                <Link to="/requests/create">Create new request</Link>
              </button>
            </div>
          </div>
          <Table data={data} />
        </>
      )}
    </div>
  )
}
